import React from 'react';
import { ReferencePill } from './ComplianceReferencePills.jsx';
import { verdictLabel } from './complianceIssuePresenters';

const docIcon = (
  <svg width="14" height="14" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <rect x="1.5" y="1" width="8" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M3.5 4.5h5M3.5 7h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

export function PanelSection({ title, children, className = '' }) {
  return (
    <section className={`panel-sidebar__section${className ? ` ${className}` : ''}`}>
      {title ? <h3 className="panel-sidebar__section-title">{title}</h3> : null}
      <div className="panel-sidebar__section-body">{children}</div>
    </section>
  );
}

export function PanelField({ label, children, required, id }) {
  return (
    <div className="panel-field" id={id}>
      <div className="panel-field__label">
        {label}
        {required ? <span className="panel-field__required">Required</span> : null}
      </div>
      <div className="panel-field__value">{children}</div>
    </div>
  );
}

export function PanelMetaItem({ label, children, fullWidth = false }) {
  return (
    <div className="panel-sidebar__meta-item">
      <span className="panel-sidebar__meta-label">{label}</span>
      <div className={`panel-sidebar__meta-value${fullWidth ? ' panel-sidebar__meta-value--full' : ''}`}>{children}</div>
    </div>
  );
}

export function PanelChip({ children }) {
  return <span className="panel-chip">{children}</span>;
}

export function PanelProse({ children }) {
  return <p className="panel-prose">{children}</p>;
}

export function PanelTextLink({ children, onClick, type = 'button', className = '', title }) {
  return (
    <button
      type={type}
      className={`panel-text-link${className ? ` ${className}` : ''}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

export function PanelDocCard({ source, onOpen }) {
  if (!source?.docName && !source?.statement) return null;
  const canOpen = !!onOpen;

  const inner = (
    <>
      <div className="panel-doc-card__title">
        {docIcon}
        <span>{source.docName || 'Document'}</span>
      </div>
      {source.location ? <p className="panel-doc-card__location">{source.location}</p> : null}
      {source.statement ? (
        <blockquote className="panel-doc-card__quote">{source.statement}</blockquote>
      ) : null}
      {canOpen ? (
        <div className="panel-doc-card__actions">
          <span className="panel-text-link panel-text-link--inline">Open document →</span>
        </div>
      ) : null}
    </>
  );

  if (canOpen) {
    return (
      <button
        type="button"
        className="panel-doc-card panel-doc-card--interactive"
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        {inner}
      </button>
    );
  }

  return <div className="panel-doc-card">{inner}</div>;
}

export function PanelAbsentDocs({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="panel-absent-list">
      {items.map((d, idx) => (
        <li key={idx} className="panel-absent-list__item">
          {docIcon}
          <span>{d.docName || d.title || 'Document'}</span>
        </li>
      ))}
    </ul>
  );
}

const openDocIcon = (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path
      d="M2 8L8 2M8 2H4M8 2v4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Detail panel zone with uppercase section title. */
export function PanelZoneSection({ title, accessory, children, zone = '2', className = '' }) {
  const zoneClass = zone === '3' ? 'panel-zone-3' : 'panel-zone-2';
  return (
    <section className={`${zoneClass}${className ? ` ${className}` : ''}`}>
      <div className="panel-zone-label-row">
        <h3 className="panel-zone-label">{title}</h3>
        {accessory || null}
      </div>
      {children}
    </section>
  );
}

/** Compliance type label + requirement reference pill (same ref as table column). */
export function PanelRequirementContext({ complianceTypeLabel, requirementRef, onRefClick }) {
  const label = (requirementRef?.label || requirementRef?.value || '').trim();
  return (
    <div className="finding-row">
      <span className="type-label">{complianceTypeLabel}</span>
      {label ? (
        <ReferencePill
          ref={requirementRef}
          onClick={requirementRef?.originalPdfKey ? onRefClick : undefined}
        />
      ) : null}
    </div>
  );
}

export function PanelRequirementBox({ children }) {
  if (!children) return null;
  return (
    <div className="requirement-box">
      <div className="requirement-text">{children}</div>
    </div>
  );
}

export function PanelVerdictRow({ verdict }) {
  if (!verdict) return null;
  return (
    <div className="verdict-row">
      <span className={`badge badge-${verdict}`}>{verdictLabel(verdict)}</span>
    </div>
  );
}

export function PanelEvidenceBox({ source, onOpen }) {
  if (!source?.docName && !source?.statement) return null;
  const canOpen = !!onOpen;
  const inner = (
    <>
      <div className="evidence-doc-name panel-content-title">
        {docIcon}
        <span>{source.docName || 'Document'}</span>
      </div>
      {source.location ? <div className="evidence-location">{source.location}</div> : null}
      {source.statement ? <div className="evidence-statement">{source.statement}</div> : null}
      {canOpen ? (
        <div className="evidence-box-footer">
          {openDocIcon}
          Open document
        </div>
      ) : null}
    </>
  );

  if (canOpen) {
    return (
      <button
        type="button"
        className="evidence-box doc-link"
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        {inner}
      </button>
    );
  }

  return <div className="evidence-box">{inner}</div>;
}

/** Resolution field block (requirement-label + control). */
export function PanelZoneField({ label, required, children, id }) {
  return (
    <div className="panel-zone-field" id={id}>
      <div className="requirement-label panel-zone-field__label">
        {label}
        {required ? <span className="field-required-indicator"> *</span> : null}
      </div>
      {children}
    </div>
  );
}

// Sidebar text links (non-table-pill style)
function PanelReferenceList({ refs, onRefClick }) {
  if (!refs?.length) {
    return <span className="panel-empty-hint">No references</span>;
  }
  return (
    <ul className="panel-ref-list">
      {refs.map((r, idx) => {
        const label = r.label || r.value || '—';
        const clickable = !!(r.originalPdfKey && onRefClick);
        const prefix = r.type ? `${r.type} · ` : '';
        return (
          <li key={`${r.type || 'ref'}-${r.value || label}-${idx}`}>
            {clickable ? (
              <button type="button" className="panel-ref-list__link" onClick={() => onRefClick(r)}>
                <span className="panel-ref-list__prefix">{prefix}</span>
                {label}
              </button>
            ) : (
              <span className="panel-ref-list__static">
                <span className="panel-ref-list__prefix">{prefix}</span>
                {label}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function PanelReferencesField({ refs, onRefClick }) {
  if (!refs?.length) return null;
  return (
    <PanelField label="Code references">
      <PanelReferenceList refs={refs} onRefClick={onRefClick} />
    </PanelField>
  );
}

export function PanelFileList({ files, onFileClick, emptyText = 'No files yet.' }) {
  if (!files?.length) {
    return <p className="panel-empty-hint">{emptyText}</p>;
  }
  return (
    <ul className="panel-file-list">
      {files.map((file, idx) => (
        <li key={file.key || file.name || idx}>
          <button type="button" className="panel-file-list__item" onClick={() => onFileClick?.(idx)}>
            {docIcon}
            <span className="panel-file-list__name">{file.name}</span>
            <span className="panel-file-list__chevron" aria-hidden="true">
              →
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
